import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { apiInstance } from "@/shared/api";
import { filterObjectKeys } from "@/shared/lib/utils";
import type {
  V2GetLeadsResponse,
  V2GetLeadsDto,
  V2ConvertLeadsDto,
  V2ConvertLeadsResponse,
  V2ReviveLeadsDto,
  V2UpdateLeadsDto,
  V2UpdateLeadsParams,
  V2CheckLeadDto,
  V2GetLeadsByUserIdDto,
  V2CreateLeadDto,
  V2CreateLeadResponse,
} from "./api.dto";

export const useGetLeads = (_data: V2GetLeadsDto) => {
  const data = {
    ..._data,
    ids: _data.ids && _data.ids?.length > 1 ? _data.ids : _data.ids?.[0],
  };
  return useQuery({
    queryKey: ["leads", data],
    queryFn: async () => {
      const response = await apiInstance.get<V2GetLeadsResponse>(
        "/leads",
        filterObjectKeys(data),
        true,
        { timeout: 50000 },
      );

      return response.data;
    },
  });
};

export const useGetLeadByUserId = (data: V2GetLeadsByUserIdDto) => {
  const params = filterObjectKeys(
    {
      ...data,
      ...(data.refIds && data.refIds.length > 0
        ? { refIds: data.refIds, userIds: null }
        : { refIds: null, userIds: data.userId }),
      sort: data.sort ? data.sort : "createdAt",
      order: data.order ? data.order : "desc",
    },
    ["userId"],
  );

  return useQuery({
    queryKey: ["lead", data],
    queryFn: async () => {
      const lead = await apiInstance.get<V2GetLeadsResponse>(
        "/leads",
        params,
        true,
      );

      return lead.data;
    },
  });
};

export const useSendLeadInfo = () => {
  return useMutation({
    mutationKey: ["sendLeadInfo"],
    mutationFn: async (leadId: string) => {
      const response = await apiInstance.post<{ message: "accepted" }>(
        `/leads/${leadId}/dispatch`,
        undefined,
        undefined,
        true,
      );

      return response.data.message;
    },
  });
};

export const useConvertLeads = () => {
  return useMutation({
    mutationKey: ["converLeads"],
    mutationFn: async (data: V2ConvertLeadsDto) => {
      const response = await apiInstance.post<V2ConvertLeadsResponse>(
        "/leads/convert",
        data,
        undefined,
        true,
      );

      return response.data;
    },
  });
};

export const useReviveLeads = () => {
  return useMutation({
    mutationKey: ["reviveLeads"],
    mutationFn: async (data: V2ReviveLeadsDto) => {
      const response = await apiInstance.post<{ message: string }>(
        "/leads/revive",
        data,
        undefined,
        true,
      );

      return response.data;
    },
  });
};

export const useUpdateLeads = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["updateLeads"],
    mutationFn: async ({
      data,
      params,
    }: {
      data?: V2UpdateLeadsDto;
      params?: V2UpdateLeadsParams;
    }) => {
      const response = await apiInstance.patch<{ message: string }>(
        "/leads",
        data,
        { params },
        true,
      );

      return response.data;
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ["leads"] });
    },
  });
};

export const useCheckLead = (data: V2CheckLeadDto) => {
  return useQuery({
    queryKey: ["lead", data],
    queryFn: async () => {
      const response = await apiInstance.head("/leads", data, true);

      return response.status === 200;
    },
    enabled: !!data.telegramId,
  });
};

export const useLeadShares = (leadId: string | undefined, enabled: boolean) => {
  return useQuery({
    queryKey: ["leadShares", leadId],
    queryFn: async () => {
      const response = await apiInstance.get<V2GetLeadsResponse>(
        "/leads",
        {
          ids: leadId,
        },
        true,
      );

      return response.data.data[0].shares;
    },
    enabled: enabled && !!leadId,
  });
};

export const useIrrelevantLeadsCount = (userId?: string) => {
  const { data } = useQuery({
    queryKey: ["irrelevantLeadsCount"],
    queryFn: async () => {
      const response = await apiInstance.get<V2GetLeadsResponse>(
        "/leads",
        filterObjectKeys({
          userIds: userId,
          pageSize: 1,
          currentPage: 1,
        }),
        true,
      );

      return response.data.total;
    },
  });

  return data;
};

export const useCreateLead = () => {
  return useMutation({
    mutationKey: ["createLead"],
    mutationFn: async (data: V2CreateLeadDto) =>
      await apiInstance.post<V2CreateLeadResponse>(
        "/leads",
        data,
        undefined,
        true,
      ),
  });
};
